// useQuotas.js
// File: nexusflow-saas/dashboard/src/hooks/useQuotas.js
// Hook for checking quotas and showing upgrade prompts

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { useAuth } from '../contexts/AuthContext';

export function useQuotas() {
  const { currentUser } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load usage stats
  const loadUsage = useCallback(async () => {
    if (!currentUser?.tenantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const getTenantUsageStats = httpsCallable(functions, 'getTenantUsageStats');
      const result = await getTenantUsageStats({ 
        tenantId: currentUser.tenantId 
      });

      if (result.data.success) {
        setUsage(result.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.tenantId]);

  // Load on mount and when tenant changes
  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  /**
   * Check if quota is available for an action
   * @param {string} quotaType - 'evaluation', 'campaign', 'share', 'pdf'
   * @returns {Promise<{available: boolean, message: string}>}
   */
  const checkQuota = async (quotaType) => {
    if (!currentUser?.tenantId) {
      return { available: false, message: 'Not authenticated' };
    }

    try {
      const checkQuotaAvailable = httpsCallable(functions, 'checkQuotaAvailable');
      const result = await checkQuotaAvailable({
        tenantId: currentUser.tenantId,
        quotaType: quotaType
      });

      return {
        available: result.data.available,
        message: result.data.message
      };
    } catch (err) {
      return {
        available: false,
        message: err.message
      };
    }
  };

  /**
   * Check if evaluation quota is available
   */
  const canEvaluate = async () => {
    return await checkQuota('evaluation');
  };

  /**
   * Check if can create new campaign
   */
  const canCreateCampaign = async () => {
    return await checkQuota('campaign');
  };

  /**
   * Check if can create share link
   */
  const canCreateShare = async () => {
    return await checkQuota('share');
  };

  /**
   * Check if can download PDF
   */
  const canDownloadPDF = async () => {
    return await checkQuota('pdf');
  };

  /**
   * Get usage for specific quota
   */
  const getUsage = (quotaType) => {
    if (!usage?.usage) return null;

    switch (quotaType) {
      case 'evaluation':
        return usage.usage.evaluations;
      case 'campaign':
        return usage.usage.campaigns;
      case 'share':
        return usage.usage.shareLinks;
      case 'pdf':
        return usage.usage.pdf;
      default:
        return null;
    }
  };

  /**
   * Check if needs upgrade (any quota exceeded)
   */
  const needsUpgrade = usage?.needsUpgrade || false;

  /**
   * Get warning status for a quota
   * Returns: 'ok', 'warning', 'exceeded'
   */
  const getWarningStatus = (quotaType) => {
    const usageData = getUsage(quotaType);
    if (!usageData) return 'ok';

    return usageData.warning || 'ok';
  };

  /**
   * Get percentage used for a quota
   */
  const getPercentageUsed = (quotaType) => {
    const usageData = getUsage(quotaType);
    if (!usageData) return 0;

    return usageData.percentage || 0;
  };

  return {
    // State
    usage: usage?.usage || null,
    plan: usage?.plan || null,
    loading,
    error,
    
    // Actions
    loadUsage,
    checkQuota,
    canEvaluate,
    canCreateCampaign,
    canCreateShare,
    canDownloadPDF,
    
    // Helpers
    getUsage,
    needsUpgrade,
    getWarningStatus,
    getPercentageUsed
  };
}